import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dayjs } from "dayjs";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { getSpecialOrders } from "../../services";
import {
  SpecialOrderListRow,
  SpecialOrderStatus,
} from "../../types/special-order.type";
import { formatNumber } from "../../utils";
import { ModeChip, StatusChip } from "./Chips";
import { UndeliveredChip } from "./UndeliveredAlert";
import SpecialOrderPagination from "./Pagination";
import { ALL } from "./Filter";
import { API_DATE_FORMAT, formatOrderDate, specialOrderDetailPath } from "./data";

interface SpecialOrderTableProps {
  companyId: string;
  dateFrom: Dayjs | null;
  dateTo: Dayjs | null;
  status: string;
  routeUuid: string;
  search: string;
}

const COLUMN_COUNT = 7;
const LIMIT = 10;

const headerCellSx = {
  borderRight: (theme: import("@mui/material/styles").Theme) =>
    `1px solid ${theme.palette.draft.main}`,
  whiteSpace: "nowrap",
};

export default function SpecialOrderTable({
  companyId,
  dateFrom,
  dateTo,
  status,
  routeUuid,
  search,
}: SpecialOrderTableProps) {
  const navigate = useNavigate();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(LIMIT);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [rows, setRows] = useState<SpecialOrderListRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const validFrom = dateFrom?.isValid() ? dateFrom.format(API_DATE_FORMAT) : undefined;
  const validTo = dateTo?.isValid() ? dateTo.format(API_DATE_FORMAT) : undefined;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await getSpecialOrders({
        page,
        limit,
        status: status === ALL ? undefined : (status as SpecialOrderStatus),
        delivery_date_from: validFrom,
        delivery_date_to: validTo,
        route_uuid: routeUuid === ALL ? undefined : routeUuid,
        q: search.trim() || undefined,
        company_id: companyId === ALL ? undefined : companyId,
      });
      setRows(data?.rows ?? []);
      setTotalCount(data?.count ?? 0);
    } catch {
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, status, validFrom, validTo, routeUuid, search, companyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Any filter change starts again from the first page.
  useEffect(() => {
    setPage(1);
  }, [status, validFrom, validTo, routeUuid, search, companyId]);

  return (
    <Paper sx={{ p: 0, overflow: "hidden" }}>
      <TableContainer>
        <Table>
          <TableHead sx={{ bgcolor: (theme) => theme.palette.background.default }}>
            <TableRow>
              <TableCell sx={headerCellSx}>เลขที่ออเดอร์</TableCell>
              <TableCell sx={headerCellSx}>ลูกค้า</TableCell>
              <TableCell sx={headerCellSx}>สาย</TableCell>
              <TableCell sx={headerCellSx}>วันที่ส่ง/รับ</TableCell>
              <TableCell sx={headerCellSx} align="center">
                สถานะ
              </TableCell>
              <TableCell sx={headerCellSx} align="right">
                ยอดสุทธิ
              </TableCell>
              <TableCell>เลขบิล</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading &&
              Array.from({ length: Math.min(limit, 10) }).map((_, row) => (
                <TableRow key={`skeleton-${row}`}>
                  {Array.from({ length: COLUMN_COUNT }).map((__, cell) => (
                    <TableCell key={cell}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบออเดอร์พิเศษตามเงื่อนไข
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              rows.map((row, index) => (
                <TableRow
                  key={row.uuid}
                  hover
                  onClick={() => navigate(specialOrderDetailPath(row.uuid))}
                  sx={{
                    cursor: "pointer",
                    bgcolor: index % 2 === 1 ? "#f6f6f9" : "#fff",
                    "& td": { padding: "8px 12px" },
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} color="primary">
                      {row.order_no}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.item_count} รายการ
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.customer?.name || "-"}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.customer?.customer_code || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <ModeChip mode={row.mode} short />
                      <Typography variant="body2">{row.route?.route_code || "-"}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatOrderDate(row.delivery_date)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={0.5}
                      flexWrap="wrap"
                    >
                      <StatusChip status={row.status} />
                      {row.status === "ordered" && row.undelivered && (
                        <UndeliveredChip undelivered={row.undelivered} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>
                      {formatNumber(row.grand_total)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {row.reserved_transaction_no ? (
                      <Typography variant="body2">{row.reserved_transaction_no}</Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        ยังไม่จอง
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box px={2} pt={1.5}>
        <Typography variant="body2" color="text.secondary">
          ทั้งหมด {totalCount} รายการ
        </Typography>
      </Box>

      <SpecialOrderPagination
        totalCount={totalCount}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
      />
    </Paper>
  );
}
